import * as React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Drawer,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ArrowCounterClockwise";
import { FolderPlusIcon } from "@phosphor-icons/react/dist/ssr/FolderPlus";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import { useCreateGroup } from "@/hooks/api/use-groups";

import { FieldType } from "../dynamicField/types";
import { FilterBuilder } from "./filter-builder";
import { FilterCondition, FilterOperator } from "./types";

interface AdvancedFiltersDrawerProps {
	open: boolean;
	onClose: () => void;
	onReset: () => void;
	onApply: (conditions: FilterCondition[]) => void;
	conditions: FilterCondition[];
	availableFields: FieldType[];
}

export function AdvancedFiltersDrawer({
	open,
	onClose,
	onReset,
	onApply,
	conditions,
	availableFields,
}: AdvancedFiltersDrawerProps): React.JSX.Element {
	const [temporaryConditions, setTemporaryConditions] = React.useState<FilterCondition[]>(conditions);
	const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = React.useState(false);
	const [groupName, setGroupName] = React.useState("");
	const [isCreatingGroup, setIsCreatingGroup] = React.useState(false);

	const createGroupMutation = useCreateGroup();

	React.useEffect(() => {
		if (open) {
			setTemporaryConditions(conditions);
		}
	}, [open, conditions]);

  const handleClose = () => {
    onClose();
    onApply(temporaryConditions);
  };

	const handleApply = () => {
		onApply(temporaryConditions);
		onClose();
	};

	const handleReset = () => {
		setTemporaryConditions([]);
		onReset();
		onClose();
	};

	const handleConditionsChange = (newConditions: FilterCondition[]) => {
		setTemporaryConditions(newConditions);
	};

	const handleCreateGroup = async () => {
		setIsCreatingGroup(true);
    
		try {
      const formattedConditions = temporaryConditions.map((condition) => {
        const isMultiple = condition.operator === FilterOperator.IN || condition.operator === FilterOperator.NOT_IN;
        if (isMultiple && Array.isArray(condition?.value)) {
          return {
            ...condition,
            value: condition?.value?.map((v) => v?.value) || [],
          };
        }
        return {
          ...condition,
          value: condition?.value?.[0]?.value || condition?.value || "",
        };
      });
			await createGroupMutation.mutateAsync({
				name: groupName.trim(),
				filter: formattedConditions,
				isStatic: false,
			});

			handleCloseCreateGroupDialog();
		} catch (error: any) {
			console.error(error);
		} finally {
			setIsCreatingGroup(false);
		}
	};

	const handleOpenCreateGroupDialog = () => {
		setIsCreateGroupDialogOpen(true);
	};

	const handleCloseCreateGroupDialog = () => {
		setIsCreateGroupDialogOpen(false);
		setGroupName("");
	};

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={handleClose}
			slotProps={{
				backdrop: { invisible: true },
				paper: {
					sx: {
						width: "600px",
						maxWidth: "100dvw",
						flex: 1,
						backgroundImage: "none",
						backgroundColor: "background.paper",
						display: "flex",
						flexDirection: "column",
					},
				},
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					py: 2,
					px: 2.5,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography variant="h6">Filters</Typography>

				<Stack direction="row" spacing={1} alignItems="center">
					<Tooltip title="Reset" placement="bottom" arrow>
						<IconButton onClick={handleReset} size="small">
							<ArrowCounterClockwiseIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Close" placement="bottom" arrow>
						<IconButton onClick={handleClose} size="small">
							<XIcon />
						</IconButton>
					</Tooltip>
				</Stack>
			</Box>

			<Box sx={{ flex: "1 1 auto", overflow: "hidden", display: "flex", flexDirection: "column" }}>
				<Stack sx={{ flex: "1 1 auto", overflow: "auto", p: 2 }}>
					<FilterBuilder
						conditions={temporaryConditions}
						onChange={handleConditionsChange}
						availableFields={availableFields}
					/>
				</Stack>

				<Box
					sx={{
						p: 2,
						borderTop: "1px solid",
						borderColor: "divider",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{temporaryConditions.length} condition{temporaryConditions.length === 1 ? "" : "s"}
					</Typography>
					<Stack direction="row" spacing={1}>
						{temporaryConditions.length > 0 && (
							<Button
								variant="outlined"
								startIcon={<FolderPlusIcon />}
								onClick={handleOpenCreateGroupDialog}
								color="secondary"
							>
								Create Group
							</Button>
						)}
						<Button variant="outlined" onClick={onClose}>
							Cancel
						</Button>
						<Button variant="contained" onClick={handleApply}>
							Apply Filters
						</Button>
					</Stack>
				</Box>
			</Box>

			{/* Dialog for creating filter group */}
			<Dialog open={isCreateGroupDialogOpen} onClose={handleCloseCreateGroupDialog} maxWidth="sm" fullWidth>
				<DialogTitle>Create Filter Group</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Group Name"
						fullWidth
						variant="outlined"
						value={groupName}
						onChange={(e) => setGroupName(e.target.value)}
						placeholder="Enter group name"
						sx={{ mt: 2 }}
					/>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						This will create a saved filter group with {temporaryConditions.length} condition
						{temporaryConditions.length === 1 ? "" : "s"}.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseCreateGroupDialog}>Cancel</Button>
					<Button
						onClick={handleCreateGroup}
						variant="contained"
						disabled={!groupName.trim()}
						loading={isCreatingGroup}
					>
						Create Group
					</Button>
				</DialogActions>
			</Dialog>
		</Drawer>
	);
}
